import type { InkStroke, PenStyle } from '@shared/notebook_model';

export type BenchTool = Extract<PenStyle, 'ink' | 'pencil' | 'marker' | 'chalk' | 'eraser'>;

export interface BenchPad {
  id: string;
  title: string;
  strokes: InkStroke[];
  updatedAt: number;
}

export interface BenchToolPrefs {
  tool: BenchTool;
  color: string;
  size: number;
}

export interface BenchDocument {
  version: 1;
  activePadId: string;
  pads: BenchPad[];
  toolPrefs: BenchToolPrefs;
}

const DB_NAME = 'ox-journal-experimental-bench';
const DB_VERSION = 1;
const STORE_DOCS = 'documents';
const DOC_KEY = 'bench';

let _db: IDBDatabase | null = null;

function makePadId(): string {
  return `bench_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
}

function defaultPads(): BenchPad[] {
  const now = Date.now();
  return [
    { id: makePadId(), title: 'Scratch A', strokes: [], updatedAt: now },
    { id: makePadId(), title: 'Scratch B', strokes: [], updatedAt: now },
    { id: makePadId(), title: 'Scratch C', strokes: [], updatedAt: now },
  ];
}

export function createDefaultBenchDocument(): BenchDocument {
  const pads = defaultPads();
  return {
    version: 1,
    activePadId: pads[0].id,
    pads,
    toolPrefs: {
      tool: 'ink',
      color: '#2c1810',
      size: 3,
    },
  };
}

function sanitizeDocument(raw: BenchDocument | null | undefined): BenchDocument {
  if (!raw) return createDefaultBenchDocument();

  const pads = Array.isArray(raw.pads) && raw.pads.length > 0
    ? raw.pads.map((pad, index) => ({
      id: typeof pad.id === 'string' && pad.id.length > 0 ? pad.id : makePadId(),
      title: typeof pad.title === 'string' && pad.title.trim().length > 0
        ? pad.title.trim()
        : `Scratch ${String.fromCharCode(65 + index)}`,
      strokes: Array.isArray(pad.strokes) ? pad.strokes : [],
      updatedAt: typeof pad.updatedAt === 'number' ? pad.updatedAt : Date.now(),
    }))
    : defaultPads();

  const activePadId = pads.some(pad => pad.id === raw.activePadId)
    ? raw.activePadId
    : pads[0].id;

  return {
    version: 1,
    activePadId,
    pads,
    toolPrefs: {
      tool: raw.toolPrefs?.tool ?? 'ink',
      color: raw.toolPrefs?.color ?? '#2c1810',
      size: typeof raw.toolPrefs?.size === 'number' ? raw.toolPrefs.size : 3,
    },
  };
}

function openDB(): Promise<IDBDatabase> {
  if (_db) return Promise.resolve(_db);

  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_DOCS)) {
        db.createObjectStore(STORE_DOCS);
      }
    };

    req.onsuccess = () => {
      _db = req.result;
      resolve(_db);
    };

    req.onerror = () => reject(req.error);
  });
}

async function idbGet<T>(store: string, key: IDBValidKey): Promise<T | undefined> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readonly');
    const req = tx.objectStore(store).get(key);
    req.onsuccess = () => resolve(req.result as T | undefined);
    req.onerror = () => reject(req.error);
  });
}

async function idbPut(store: string, key: IDBValidKey, value: unknown): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite');
    tx.objectStore(store).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function loadBenchDocument(): Promise<BenchDocument> {
  const stored = await idbGet<BenchDocument>(STORE_DOCS, DOC_KEY);
  return sanitizeDocument(stored);
}

export async function saveBenchDocument(doc: BenchDocument): Promise<void> {
  await idbPut(STORE_DOCS, DOC_KEY, sanitizeDocument(doc));
}
